import { Link } from '@tanstack/react-router';
import {
  Activity,
  AlertOctagon,
  ArrowRight,
  Clock,
  Coins,
  Gauge,
  RadioTower,
  RefreshCw,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type {
  AmountBucket,
  DashboardMetrics,
  ProviderQuota,
  ProviderQuotaStatus,
  ReconHealthRow,
} from '@pf/contracts';
import {
  useAmountDistribution,
  useAmountScatter,
  useDashboardMetrics,
  useLedgerDivergences,
  useProviderQuota,
  useReconHealth,
  type MetricsRange,
} from '@/lib/api/ops-queries';
import { hours, nowMs, toIso } from '@/lib/clock';
import { formatDate, formatDateTimeCompact, formatRelative, msSince } from '@/lib/datetime';
import { formatDecimalString, formatMoney, parseDecimalString } from '@/lib/money';
import { DEPOSIT_STATE_META, TONE_CLASSES, WITHDRAWAL_STATE_META } from '@/lib/states';
import { cn } from '@/lib/utils';
import { AmountHistogram, type AmountHistogramLabels } from '@/components/charts/amount-histogram';
import { AmountScatter, type AmountScatterLabels } from '@/components/charts/amount-scatter';
import {
  MultiLineChart,
  StateBarChart,
  TrendChart,
  type StateBarChartLabels,
} from '@/components/charts/charts';
import { StatTile } from '@/components/charts/stat-tile';
import { ParticipantChip } from '@/components/domain/participant-chip';
import { WebScopeScopedNotice } from '@/components/domain/web-scope-select';
import { PageHeader, Section } from '@/components/layout/page';
import { CustomRangePicker, windowButtonClasses } from '@/components/layout/range-picker';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, EmptyState, ErrorState, Skeleton } from '@/components/ui/feedback';
import { Pill, Progress } from '@/components/ui/primitives';

/** Quick-select KPI windows, in hours (the API's unit): a day, a week, a month. */
const WINDOWS = [
  { hours: 24, labelKey: 'dashboard.window.day' },
  { hours: 168, labelKey: 'dashboard.window.week' },
  { hours: 720, labelKey: 'dashboard.window.month' },
] as const;

/** The window the dashboard opens on, and the one clearing a custom range falls back to. */
const DEFAULT_RANGE: MetricsRange = { kind: 'preset', hours: 24 };

/**
 * "24 hours" / "7 days" / "35 minutes" — the length of a window, spoken.
 *
 * `window_hours` is no longer an integer: the contract relaxed it so a custom range that runs from
 * 08:15 to 18:00 can report its true 9.75 h rather than a rounded fiction. That makes two failure
 * modes possible here which could not happen when every window was a preset — a sub-hour range
 * printing as "0 hours", and a ragged one printing as "1.4166666666 hours" — so this rounds to a
 * single decimal and drops to minutes below the hour. The days form is still reserved for exact
 * multiples, because "1.3 days" is harder to picture than "31.2 hours".
 */
function windowText(t: TFunction, hours: number): string {
  if (hours > 24 && hours % 24 === 0) return t('dashboard.window.days', { count: hours / 24 });
  if (hours < 1) return t('common.minutes', { count: Math.max(1, Math.round(hours * 60)) });
  return t('dashboard.window.hours', { count: Math.round(hours * 10) / 10 });
}

/**
 * Translate every string a state-census bar chart renders, for one of the two machines.
 *
 * The census section draws the *same* `StateBarChart` twice, over two different populations, and
 * the series name is the one string that has to differ between them: a withdrawal bar labelled
 * "transactions" is not a translation problem, it is a wrong chart. Only this page knows which of
 * the two machines it is rendering, so only this page can name the bars — see rule 5 in
 * `components/charts/charts.tsx`.
 *
 * The series name is deliberately the *card's own title* rather than a key of its own. The heading
 * above the chart and the measure named inside its tooltip are the same noun by construction, so
 * giving them one key means they cannot drift into disagreeing about what is being counted, in any
 * locale. The two column headers are shared, because the text equivalent's shape does not depend
 * on which machine filled it in.
 *
 * @param t - the translator.
 * @param machine - which state machine's census is being drawn.
 */
function censusLabels(t: TFunction, machine: 'deposit' | 'withdrawal'): StateBarChartLabels {
  const group = 'dashboard.stateCensus';
  return {
    series: t(`${group}.${machine}Title`),
    state: t(`${group}.chartLabels.state`),
    count: t(`${group}.chartLabels.count`),
  };
}

/**
 * Dashboard — the SAD §13 KPIs.
 *
 * §13 names eight business KPIs and this page shows all eight, but it is not a wall of numbers.
 * It is ordered by the question an operator arrives with:
 *
 * 1. *Is money flowing?* — reservations/s and match success, at the top.
 * 2. *Is verification healthy?* — the pass/review/reject split, which is the leading indicator
 *    §13 says to page on.
 * 3. *Is anything stuck?* — open reviews and DLQ depth, with a link straight into the work.
 * 4. *Where is the money concentrated?* — the settled-amount histogram.
 * 5. *Where is everything?* — the state census across both machines.
 *
 * Question 4 sits where it does because of what kind of question it is. The three above it are
 * asked under pressure — something may be on fire, and the answer decides whether to page someone.
 * Nobody has ever been paged because the median deposit moved. Concentration is the question an
 * operator asks when *nothing* is wrong: which amounts to set limits at, where a fee band would
 * actually bite, whether the pool is carried by many small deposits or a few large ones. Putting
 * it above verification would push the leading indicator §13 says to page on below the fold to
 * make room for a planning question; putting it below the census would bury it under an
 * inventory. So it goes last among the money sections and first among the "look around" ones.
 *
 * The DLQ tile is deliberately louder than the others: §13 says "DLQ is an incident, not a
 * metric", and a dashboard that renders a non-zero DLQ in the same grey as everything else is
 * disagreeing with its own architecture.
 */
export function DashboardPage(): ReactNode {
  const { t } = useTranslation();
  const [range, setRange] = useState<MetricsRange>(DEFAULT_RANGE);
  const metrics = useDashboardMetrics(range);

  if (metrics.isLoading) return <DashboardSkeleton />;
  if (metrics.isError) {
    return (
      <>
        <PageHeader title={t('dashboard.title')} />
        <ErrorState
          error={metrics.error}
          onRetry={() => {
            void metrics.refetch();
          }}
        />
      </>
    );
  }
  if (!metrics.data) return null;

  const data = metrics.data;

  // The window's name, for the description and every chart's text equivalent.
  //
  // Preferably read off the *response*, so the sentence and the numbers under it always describe
  // the same window — and for a custom range that means `range_start`/`range_end`, rendered back
  // in the operator's local zone, rather than the "N hours" that would make an operator do the
  // arithmetic the picker just did for them. The exception is the frame where `keepPreviousData`
  // is still holding the outgoing window on screen: the payload then describes the *previous*
  // selection, so the label falls back to what was just asked for rather than captioning the new
  // selection with the old window's bounds.
  const settled = metrics.isPlaceholderData ? null : data;
  const window =
    range.kind === 'custom'
      ? t('dashboard.window.range', {
          from: formatDateTimeCompact(settled?.range_start ?? range.from),
          to: formatDateTimeCompact(settled?.range_end ?? range.to),
        })
      : t('dashboard.window.last', {
          duration: windowText(t, settled?.window_hours ?? range.hours),
        });

  const dlqDepth = data.dlq_depth.current;
  const circuitTone =
    data.provider_circuit === 'CLOSED'
      ? 'good'
      : data.provider_circuit === 'HALF_OPEN'
        ? 'attention'
        : 'bad';

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.description', { window })}
        badge={
          <Pill className={cn(TONE_CLASSES[circuitTone].badge, 'rounded-full')}>
            <ShieldCheck className="size-3" aria-hidden="true" />
            {t('dashboard.circuitBadge', {
              circuit: data.provider_circuit.toLowerCase().replace('_', '-'),
            })}
          </Pill>
        }
        actions={
          <>
            <div
              role="group"
              aria-label={t('dashboard.window.label')}
              className="flex items-center gap-1.5"
            >
              {WINDOWS.map((candidate) => (
                <button
                  key={candidate.hours}
                  type="button"
                  aria-pressed={range.kind === 'preset' && range.hours === candidate.hours}
                  onClick={() => {
                    setRange({ kind: 'preset', hours: candidate.hours });
                  }}
                  className={windowButtonClasses(
                    range.kind === 'preset' && range.hours === candidate.hours,
                  )}
                >
                  {t(candidate.labelKey)}
                </button>
              ))}
              <CustomRangePicker
                range={range}
                onApply={setRange}
                onClear={() => {
                  setRange(DEFAULT_RANGE);
                }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              loading={metrics.isFetching}
              onClick={() => {
                void metrics.refetch();
              }}
            >
              <RefreshCw aria-hidden="true" />
              {t('dashboard.refresh')}
            </Button>
          </>
        }
      />

      {/*
       * Every KPI, chart and tile below already narrows to the console-wide scope — the query
       * hooks apply it, not this page (`lib/api/ops-queries.ts`) — which is exactly what makes it
       * invisible without this. A dashboard is the screen an operator opens to ask "is the
       * *platform* healthy?", so a scoped one has to say so before the first number, or a bad
       * afternoon on one site reads as a bad afternoon everywhere.
       */}
      <WebScopeScopedNotice />

      {dlqDepth > 0 ? (
        <Callout
          tone="danger"
          title={t('dashboard.dlqCallout.title', { count: dlqDepth })}
        >
          <Trans i18nKey="dashboard.dlqCallout.body" />{' '}
          <Link to="/ops/admin" className="font-medium underline underline-offset-2">
            <Trans i18nKey="dashboard.dlqCallout.link" />
          </Link>
          .
        </Callout>
      ) : null}

      {/* 1 — is money flowing? */}
      <Section
        title={t('dashboard.throughput.title')}
        description={t('dashboard.throughput.description')}
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label={t('dashboard.throughput.reservationsPerSecond.label')}
            value={data.reservations_per_second.current.toFixed(2)}
            current={data.reservations_per_second.current}
            previous={data.reservations_per_second.previous}
            higherIsBetter
            hint={t('dashboard.throughput.reservationsPerSecond.hint')}
            chart={
              <TrendChart
                points={data.reservations_per_second.points}
                description={t('dashboard.throughput.reservationsPerSecond.chartDescription', {
                  window,
                })}
                seriesName={t('dashboard.throughput.reservationsPerSecond.label')}
                colorIndex={1}
                height={64}
                valueFormatter={(v) => v.toFixed(1)}
              />
            }
          />
          <StatTile
            label={t('dashboard.throughput.matchSuccess.label')}
            value={`${data.match_success_rate.current.toFixed(1)}%`}
            current={data.match_success_rate.current}
            previous={data.match_success_rate.previous}
            higherIsBetter
            hint={t('dashboard.throughput.matchSuccess.hint')}
            chart={
              <TrendChart
                points={data.match_success_rate.points}
                description={t('dashboard.throughput.matchSuccess.chartDescription', { window })}
                seriesName={t('dashboard.throughput.matchSuccess.label')}
                colorIndex={3}
                height={64}
                valueFormatter={(v) => `${String(Math.round(v))}%`}
              />
            }
          />
          <StatTile
            label={t('dashboard.throughput.releaseRate.label')}
            value={`${data.reservation_release_rate.current.toFixed(1)}%`}
            current={data.reservation_release_rate.current}
            previous={data.reservation_release_rate.previous}
            higherIsBetter={false}
            hint={t('dashboard.throughput.releaseRate.hint')}
            chart={
              <TrendChart
                points={data.reservation_release_rate.points}
                description={t('dashboard.throughput.releaseRate.chartDescription', { window })}
                seriesName={t('dashboard.throughput.releaseRate.label')}
                colorIndex={4}
                height={64}
                valueFormatter={(v) => `${String(Math.round(v))}%`}
              />
            }
          />
          <StatTile
            label={t('dashboard.throughput.settledVolume.label')}
            value={formatMoney(data.settled_volume, { withSymbol: true, decimalPlaces: 0 })}
            hint={t('dashboard.throughput.settledVolume.hint')}
            badge={
              <Pill>
                <Zap className="size-3" aria-hidden="true" />
                THB
              </Pill>
            }
          />
        </div>
      </Section>

      {/* 2 — is verification healthy? */}
      <Section
        title={t('dashboard.verification.title')}
        description={t('dashboard.verification.description')}
      >
        <div className="grid gap-3 lg:grid-cols-3">
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>{t('dashboard.verification.outcomesTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <OutcomeBar outcomes={data.verification_outcomes} />
              <p className="text-2xs leading-relaxed text-muted-foreground">
                {t('dashboard.verification.outcomesNote')}
              </p>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{t('dashboard.verification.latencyTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <MultiLineChart
                description={t('dashboard.verification.latencyChartDescription', { window })}
                valueFormatter={(v) => `${String(Math.round(v))}ms`}
                series={[
                  { name: 'p50', points: data.provider_latency_ms.p50.points, colorIndex: 2 },
                  { name: 'p95', points: data.provider_latency_ms.p95.points, colorIndex: 4 },
                  { name: 'p99', points: data.provider_latency_ms.p99.points, colorIndex: 5 },
                ]}
              />
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <StatTile
            label={t('dashboard.verification.webhookSuccess.label')}
            value={`${data.webhook_success_rate.current.toFixed(2)}%`}
            current={data.webhook_success_rate.current}
            previous={data.webhook_success_rate.previous}
            higherIsBetter
            hint={t('dashboard.verification.webhookSuccess.hint')}
          />
          <StatTile
            label={t('dashboard.verification.dlqDepth.label')}
            value={String(dlqDepth)}
            current={data.dlq_depth.current}
            previous={data.dlq_depth.previous}
            higherIsBetter={false}
            hint={t('dashboard.verification.dlqDepth.hint')}
            className={dlqDepth > 0 ? 'border-state-bad/40 bg-state-bad-bg/30' : undefined}
            badge={
              dlqDepth > 0 ? (
                <Pill className="bg-state-bad-bg text-state-bad">
                  <AlertOctagon className="size-3" aria-hidden="true" />
                  {t('dashboard.verification.dlqDepth.badge')}
                </Pill>
              ) : undefined
            }
          />
          <StatTile
            label={t('dashboard.verification.openReviews.label')}
            value={String(data.open_reviews.current)}
            current={data.open_reviews.current}
            previous={data.open_reviews.previous}
            higherIsBetter={false}
            hint={t('dashboard.verification.openReviews.hint')}
            badge={
              <Button asChild variant="ghost" size="sm" className="-my-1 h-7">
                <Link to="/ops/reviews">
                  {t('dashboard.verification.openReviews.link')}
                  <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
            }
          />
        </div>

        <ProviderQuotaCard />
      </Section>

      {/*
       * 2.5 — is the pipe to each website still healthy? (2026-08-27 observability pair)
       *
       * Neither card here is a money-path signal — SAD §13's eight KPIs are all above this line
       * already. What belongs here instead is "is PF still hearing from every website's own
       * reconciliation the way it should be", which is a different question from "is verification
       * healthy" and from "where did the money go": a website can poll on schedule, verify every
       * slip cleanly, and still be quietly diverging from PF's own ledger. Placed after
       * verification (still a live/polling question an operator pages on) and before the
       * historical concentration/census sections below, which is what "near the other live cards,
       * not the historical ones" means in practice.
       */}
      <Section
        title={t('dashboard.reconHealth.sectionTitle')}
        description={t('dashboard.reconHealth.sectionDescription')}
      >
        <div className="grid gap-3 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ReconHealthCard />
          </div>
          <LedgerDivergenceStatTile />
        </div>
      </Section>

      {/* 4 — where is the money concentrated? */}
      <AmountConcentrationSection range={range} window={window} />

      {/* 5 — where is everything? */}
      <Section
        title={t('dashboard.stateCensus.title')}
        description={t('dashboard.stateCensus.description')}
      >
        <div className="grid gap-3 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="size-4 text-muted-foreground" aria-hidden="true" />
                {t('dashboard.stateCensus.depositTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StateBarChart
                description={t('dashboard.stateCensus.depositChartDescription')}
                labels={censusLabels(t, 'deposit')}
                data={Object.entries(data.deposit_state_counts).map(([status, value]) => {
                  const meta = DEPOSIT_STATE_META[status as keyof typeof DEPOSIT_STATE_META];
                  return {
                    label: meta ? t(meta.labelKey) : status,
                    value,
                    color: toneColor(meta?.tone ?? 'idle'),
                  };
                })}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="size-4 text-muted-foreground" aria-hidden="true" />
                {t('dashboard.stateCensus.withdrawalTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StateBarChart
                description={t('dashboard.stateCensus.withdrawalChartDescription')}
                labels={censusLabels(t, 'withdrawal')}
                data={Object.entries(data.withdrawal_state_counts).map(([status, value]) => {
                  const meta =
                    WITHDRAWAL_STATE_META[status as keyof typeof WITHDRAWAL_STATE_META];
                  return {
                    label: meta ? t(meta.labelKey) : status,
                    value,
                    color: toneColor(meta?.tone ?? 'idle'),
                  };
                })}
              />
            </CardContent>
          </Card>
        </div>
      </Section>

      <p className="text-2xs text-muted-foreground">
        {t('dashboard.footer', { when: formatRelative(data.generated_at) })}
      </p>
    </>
  );
}

/** How a bucket is weighed: by how many deposits landed in it, or by how much money did. */
type AmountMeasure = 'count' | 'volume';

/** The measure toggle's two options, in the order they are offered. */
const MEASURES = [
  { measure: 'count', labelKey: 'dashboard.amountDistribution.measure.count' },
  { measure: 'volume', labelKey: 'dashboard.amountDistribution.measure.volume' },
] as const satisfies readonly { measure: AmountMeasure; labelKey: string }[];

/**
 * Name a bucket from its edges — `1,000–2,000`, and `10,000+` for the open one.
 *
 * The top bucket has no upper edge (`upper: null`), which is the whole reason this is a function
 * and not a template: an axis reading "10000–null" is the kind of thing that ships. Both edges go
 * through `formatDecimalString`, so they are grouped for legibility and the label agrees
 * digit-for-digit with the amounts rendered everywhere else in the console — no `Intl.NumberFormat`,
 * because that would take a `number` and this is money (SAD §8, R3).
 *
 * No symbol and no minor units on the tick itself. The edges are round numbers by construction, so
 * `฿10,000.00+` would spend five characters per tick restating what the value axis, the tooltip and
 * the tile beside it all already say — on the one axis where every label is repeated eight times.
 *
 * @param t - the translator, so the range and open forms stay in the locale files.
 * @param bucket - the bucket to name.
 * @param currency - the distribution's currency, which decides the grouping and scale.
 */
function bucketLabel(t: TFunction, bucket: AmountBucket, currency: string): string {
  const lower = formatDecimalString(bucket.lower, currency, { decimalPlaces: 0 });
  if (bucket.upper === null) return t('dashboard.amountDistribution.bucket.open', { lower });
  return t('dashboard.amountDistribution.bucket.range', {
    lower,
    upper: formatDecimalString(bucket.upper, currency, { decimalPlaces: 0 }),
  });
}

/**
 * Turn a bucket's exact volume into the `number` the histogram plots. The only lossy step here.
 *
 * `AmountHistogram` takes `volume: number` — a bar length is a float on a pixel scale, and there
 * is no honest way around that. What this function buys is *where* the loss happens: the wire
 * string is parsed exactly into a scaled `bigint` first (`lib/money.ts`), so a malformed amount
 * throws instead of quietly becoming `NaN`, and the float exists only inside the chart. Nothing
 * upstream — the tiles, the labels, the totals — ever sees it; those render the decimal strings
 * directly, which is why the numbers beside the chart are exact even though the bars are not.
 *
 * @param volume - the bucket's summed amount, as the wire's decimal string.
 */
function volumeToPlotValue(volume: string): number {
  const { units, scale } = parseDecimalString(volume);
  return Number(units) / 10 ** scale;
}

/**
 * Translate every string the histogram renders, for one measure.
 *
 * The chart components under `components/charts` never call `useTranslation` — they take finished
 * text, the way they already take `description`. So the section that owns the toggle owns the
 * words too, and this is where the locale keys live.
 *
 * The three measure-dependent labels are resolved *here*, not inside the chart, because only the
 * whole sentence is translatable. "Cumulative share of transactions" is not a frame plus a noun:
 * Thai reorders the modifier and drops the preposition, so a component splicing a translated word
 * into a translated frame would be composing grammar it cannot see. Resolving at the call site
 * costs one ternary per label and buys locale files that a translator can read end to end.
 *
 * `transactions`/`volume` do double duty as the two column headers and — through `measureName` —
 * as the active series' name. That is intentional: the bar legend and the count column have to
 * agree word for word, or the text equivalent stops describing the chart beside it. Note these
 * are *not* the toggle's own labels (`measure.count` is "Count", not "Transactions"): the button
 * names a view, the chart names the thing being counted.
 *
 * @param t - the translator.
 * @param measure - the measure the bars currently encode.
 */
function histogramLabels(t: TFunction, measure: AmountMeasure): AmountHistogramLabels {
  const group = 'dashboard.amountDistribution.chartLabels';
  return {
    amountRange: t(`${group}.amountRange`),
    transactions: t(`${group}.transactions`),
    volume: t(`${group}.volume`),
    cumulativeShare: t(`${group}.cumulativeShare`),
    cumulativeShareOfMeasure: t(`${group}.cumulativeShareOf.${measure}`),
    measureName: t(measure === 'count' ? `${group}.transactions` : `${group}.volume`),
    measurePerBucket: t(`${group}.perBucket.${measure}`),
    cumulativeShareAxis: t(`${group}.cumulativeShareAxis`),
    empty: t(`${group}.empty`),
  };
}

/**
 * "Where is the money concentrated?" — settled deposits bucketed by amount.
 *
 * The count/volume toggle is the feature, not a convenience. A deposit book is right-skewed
 * almost everywhere: thousands of small top-ups make up nearly all the *transactions* while a
 * few large ones make up nearly all the *money*, so the two views of the same window peak in
 * different places. An operator who has only ever seen the count view will set a limit, a fee
 * band or a review threshold against the crowd and miss the value; one who has only seen volume
 * will do the opposite. Flipping the toggle shows both without changing anything else on screen,
 * which is what makes the swap legible rather than a fact somebody has to be told.
 *
 * It owns its own query rather than reading a field off the metrics payload, and therefore its
 * own loading, error and empty states: a histogram that fails is a section that says so, not a
 * dashboard that stops answering "is money flowing?". The window comes from the page, so the two
 * always describe the same span.
 */
function AmountConcentrationSection({
  range,
  window,
}: {
  /** The window selection the KPIs above are showing. */
  range: MetricsRange;
  /** That window's human name, for the chart's text equivalent. */
  window: string;
}): ReactNode {
  const { t } = useTranslation();
  const [measure, setMeasure] = useState<AmountMeasure>('count');
  const distribution = useAmountDistribution(range);
  const data = distribution.data;

  return (
    <Section
      title={t('dashboard.amountDistribution.title')}
      description={t('dashboard.amountDistribution.description')}
      actions={
        <div
          role="group"
          aria-label={t('dashboard.amountDistribution.measure.label')}
          className="flex items-center gap-1.5"
        >
          {MEASURES.map((candidate) => (
            <button
              key={candidate.measure}
              type="button"
              aria-pressed={measure === candidate.measure}
              onClick={() => {
                setMeasure(candidate.measure);
              }}
              className={windowButtonClasses(measure === candidate.measure)}
            >
              {t(candidate.labelKey)}
            </button>
          ))}
        </div>
      }
    >
      {distribution.isLoading ? (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      ) : distribution.isError ? (
        <ErrorState
          error={distribution.error}
          onRetry={() => {
            void distribution.refetch();
          }}
        />
      ) : !data ? null : data.total_count === 0 ? (
        // A window in which nothing settled is a real answer, not a failure — and the tiles would
        // otherwise render a median of ฿0, which reads as a number rather than as an absence.
        <EmptyState
          icon={<Coins className="size-5" aria-hidden="true" />}
          title={t('dashboard.amountDistribution.empty.title')}
          description={t('dashboard.amountDistribution.empty.description')}
        />
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile
              label={t('dashboard.amountDistribution.median.label')}
              value={formatDecimalString(data.median, data.currency, {
                withSymbol: true,
                decimalPlaces: 0,
              })}
              hint={t('dashboard.amountDistribution.median.hint')}
            />
            <StatTile
              label={t('dashboard.amountDistribution.p90.label')}
              value={formatDecimalString(data.p90, data.currency, {
                withSymbol: true,
                decimalPlaces: 0,
              })}
              hint={t('dashboard.amountDistribution.p90.hint')}
            />
            {/*
             * The third tile follows the toggle. It is the total the bars add up to, so showing
             * the count total beside volume bars — or the reverse — would invite an operator to
             * check one against the other and find they disagree.
             */}
            <StatTile
              label={t(`dashboard.amountDistribution.total.${measure}Label`)}
              value={
                measure === 'count'
                  ? String(data.total_count)
                  : formatDecimalString(data.total_volume, data.currency, {
                      withSymbol: true,
                      decimalPlaces: 0,
                    })
              }
              hint={t(`dashboard.amountDistribution.total.${measure}Hint`)}
              badge={
                measure === 'volume' ? (
                  <Pill>
                    <Zap className="size-3" aria-hidden="true" />
                    {data.currency}
                  </Pill>
                ) : undefined
              }
            />
          </div>

          <Card>
            <CardContent className="pt-6">
              <AmountHistogram
                data={data.buckets.map((bucket) => ({
                  label: bucketLabel(t, bucket, data.currency),
                  count: bucket.count,
                  volume: volumeToPlotValue(bucket.volume),
                }))}
                measure={measure}
                currency={data.currency}
                description={t(`dashboard.amountDistribution.chartDescription.${measure}`, {
                  window,
                })}
                labels={histogramLabels(t, measure)}
              />
            </CardContent>
          </Card>

          {/*
           * Nested inside the populated branch on purpose. The scatter draws the *same population*
           * as the bars above it, so a window in which nothing settled has nothing for it to plot
           * either, and a second "nothing here" panel under the first would be the same sentence
           * twice. It keeps its own query and its own failure, though — see the component.
           */}
          <ArrivalScatterCard range={range} window={window} />
        </div>
      )}
    </Section>
  );
}

/**
 * Translate every string the scatter renders. Same contract as {@link histogramLabels}.
 *
 * `deposits` is the one label worth reading twice: it heads the count column of the chart's text
 * equivalent, and what it counts is the *marks that were drawn* — which, once the window is busy
 * enough to be sampled, is not the same number as the deposits that settled. Hence "Deposits
 * shown" rather than "Deposits". The note under the chart carries the real total.
 *
 * @param t - the translator.
 */
function scatterLabels(t: TFunction): AmountScatterLabels {
  const group = 'dashboard.amountScatter.chartLabels';
  return {
    time: t(`${group}.time`),
    amount: t(`${group}.amount`),
    deposits: t(`${group}.deposits`),
    largest: t(`${group}.largest`),
    empty: t(`${group}.empty`),
  };
}

/**
 * "When did it arrive?" — the same settled deposits, against the clock.
 *
 * The card below the histogram, and its own query. The histogram answers *which amounts* and
 * cannot answer *when*: bucketing throws the clock away, so a payroll run that lands in one
 * ten-minute burst and the same deposits spread across a day produce identical bars. This is the
 * chart that separates them, and it is also where a lone outlier is visible as an outlier rather
 * than as a `10,000+` bar of height one.
 *
 * It fetches separately from the histogram — its payload is thousands of points against the
 * histogram's eight buckets, and a slow or failed scatter must not take the bars down with it.
 * The window comes from the page, so all three panels describe the same span.
 *
 * ## The sample is stated, never implied
 *
 * The endpoint caps what it returns and reports the true population alongside it. The note under
 * the chart therefore always prints a count, and prints *both* counts when they differ: an
 * operator reading density off a plot is reading it off the marks, and a plot that had silently
 * dropped nine in ten of them while looking exactly as authoritative is the failure mode this
 * note exists to prevent.
 */
function ArrivalScatterCard({
  range,
  window,
}: {
  /** The window selection the rest of the dashboard is showing. */
  range: MetricsRange;
  /** That window's human name, for the chart's text equivalent. */
  window: string;
}): ReactNode {
  const { t } = useTranslation();
  const scatter = useAmountScatter(range);

  if (scatter.isLoading) return <Skeleton className="h-80" />;
  if (scatter.isError) {
    return (
      <ErrorState
        error={scatter.error}
        onRetry={() => {
          void scatter.refetch();
        }}
      />
    );
  }
  if (!scatter.data) return null;

  const data = scatter.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="size-4 text-muted-foreground" aria-hidden="true" />
          {t('dashboard.amountScatter.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <AmountScatter
          data={data.points}
          rangeStart={data.range_start}
          rangeEnd={data.range_end}
          currency={data.currency}
          description={t('dashboard.amountScatter.chartDescription', { window })}
          labels={scatterLabels(t)}
        />
        {/*
         * Not `aria-hidden`, and not folded into the chart's `description`: this is a caveat about
         * what the plot is, and it has to reach a screen-reader user reading the table above it
         * just as plainly as it reaches someone looking at the marks.
         */}
        <p className="text-2xs leading-relaxed text-muted-foreground">
          {data.sampled
            ? t('dashboard.amountScatter.note.sampled', {
                shown: data.points.length,
                total: data.total_count,
              })
            : t('dashboard.amountScatter.note.all', { total: data.total_count })}
        </p>
      </CardContent>
    </Card>
  );
}

/** Map a state tone onto the CSS variable the charts should paint with. */
function toneColor(tone: 'flight' | 'attention' | 'good' | 'bad' | 'idle'): string {
  return `hsl(var(--state-${tone}))`;
}

function OutcomeBar({
  outcomes,
}: {
  outcomes: DashboardMetrics['verification_outcomes'];
}): ReactNode {
  const { t } = useTranslation();
  const rows = [
    {
      key: 'pass',
      label: t('dashboard.outcomes.autoApproved'),
      value: outcomes.pass,
      tone: 'good' as const,
    },
    {
      key: 'review',
      label: t('dashboard.outcomes.sentToReview'),
      value: outcomes.review,
      tone: 'attention' as const,
    },
    {
      key: 'reject',
      label: t('dashboard.outcomes.rejected'),
      value: outcomes.reject,
      tone: 'bad' as const,
    },
  ];
  return (
    <div className="space-y-3">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
        {rows.map((row) => (
          <span
            key={row.key}
            className={TONE_CLASSES[row.tone].dot}
            style={{ width: `${String(row.value)}%` }}
          />
        ))}
      </div>
      <dl className="space-y-2">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-2 text-xs">
            <span
              className={cn('size-2 shrink-0 rounded-full', TONE_CLASSES[row.tone].dot)}
              aria-hidden="true"
            />
            <dt className="flex-1 text-muted-foreground">{row.label}</dt>
            <dd className="tnum font-medium">{row.value.toFixed(1)}%</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Map a provider-quota status onto the five-tone vocabulary every other state in the console uses. */
const QUOTA_STATUS_TONE: Readonly<Record<ProviderQuotaStatus, 'good' | 'attention' | 'bad' | 'idle'>> = {
  OK: 'good',
  LOW: 'attention',
  EXHAUSTED: 'bad',
  UNAVAILABLE: 'idle',
};

/**
 * Row-container classes per tone, spelled out in full rather than built with string concatenation
 * — Tailwind's JIT scanner only picks up class names that appear literally in the source, and a
 * computed `border-${tone}` string would compile to nothing.
 */
const QUOTA_ROW_CLASSES: Readonly<Record<'good' | 'attention' | 'bad' | 'idle', string>> = {
  good: 'border-border/60',
  attention: 'border-state-attention/30 bg-state-attention-bg/30',
  bad: 'border-state-bad/30 bg-state-bad-bg/30',
  idle: 'border-state-idle/30 bg-state-idle-bg/30',
};

/**
 * Slips consumed out of the period's limit, as a whole percentage — the number `Progress` and the
 * "% used" figure both plot. `null` when it cannot be computed, which is always true for an
 * `UNAVAILABLE` row (no `slips_limit` to divide by) and would also cover a `0` limit if one were
 * ever reported.
 */
function percentUsed(provider: ProviderQuota): number | null {
  if (provider.slips_used === null || provider.slips_limit === null || provider.slips_limit <= 0) {
    return null;
  }
  return Math.round((provider.slips_used / provider.slips_limit) * 100);
}

/** One provider's card inside {@link ProviderQuotaCard} — see that component for the contract. */
function ProviderQuotaRow({ provider }: { provider: ProviderQuota }): ReactNode {
  const { t } = useTranslation();
  const tone = QUOTA_STATUS_TONE[provider.status];
  const used = percentUsed(provider);

  const positionLabel =
    provider.position === 1
      ? t('dashboard.providerQuota.position.primary')
      : provider.position === null
        ? t('dashboard.providerQuota.position.notInChain')
        : t('dashboard.providerQuota.position.fallback', { position: provider.position });

  return (
    <div className={cn('space-y-3 rounded-lg border p-3.5', QUOTA_ROW_CLASSES[tone])}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold capitalize text-foreground">
            {provider.provider}
          </span>
          <Pill className={TONE_CLASSES[provider.position === 1 ? 'flight' : 'idle'].badge}>
            {positionLabel}
          </Pill>
        </div>
        <Pill className={TONE_CLASSES[tone].badge}>
          {t(`dashboard.providerQuota.status.${provider.status}`)}
        </Pill>
      </div>

      {provider.status === 'UNAVAILABLE' ? (
        // A blank or hidden row here would read as "this provider is fine" when the truth is
        // PaymentFlow simply could not ask it — not knowing the fallback's headroom is itself the
        // signal an operator needs, so this renders as prominently as the healthy rows do.
        <p className={cn('flex items-start gap-2 text-sm', TONE_CLASSES.idle.text)}>
          <AlertOctagon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {provider.error_code
            ? t('dashboard.providerQuota.unavailable.withCode', { code: provider.error_code })
            : t('dashboard.providerQuota.unavailable.noCode')}
        </p>
      ) : (
        <>
          <Progress
            value={used ?? 0}
            aria-label={t('dashboard.providerQuota.meterAriaLabel', {
              provider: provider.provider,
            })}
            indicatorClassName={TONE_CLASSES[tone].dot}
          />
          <dl className="grid grid-cols-3 gap-2 text-center">
            <div className="space-y-0.5 rounded-md bg-muted/40 py-1.5">
              <dt className="text-2xs text-muted-foreground">
                {t('dashboard.providerQuota.used')}
              </dt>
              <dd className="tnum text-sm font-semibold text-foreground">
                {provider.slips_used ?? '—'}
              </dd>
            </div>
            <div className="space-y-0.5 rounded-md bg-muted/40 py-1.5">
              <dt className="text-2xs text-muted-foreground">
                {t('dashboard.providerQuota.remaining')}
              </dt>
              <dd className="tnum text-sm font-semibold text-foreground">
                {provider.slips_remaining ?? '—'}
              </dd>
            </div>
            <div className="space-y-0.5 rounded-md bg-muted/40 py-1.5">
              <dt className="text-2xs text-muted-foreground">
                {t('dashboard.providerQuota.percentUsed')}
              </dt>
              <dd className={cn('tnum text-sm font-semibold', TONE_CLASSES[tone].text)}>
                {used !== null ? `${String(used)}%` : '—'}
              </dd>
            </div>
          </dl>
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-2xs text-muted-foreground">
            <span>
              {t('dashboard.providerQuota.package', { name: provider.package_name ?? '—' })}
            </span>
            <span className="tnum">
              {t('dashboard.providerQuota.expires', {
                date: provider.expires_at ? formatDate(provider.expires_at) : '—',
              })}
            </span>
          </div>
          {/*
           * Slip2Go meters its own portal in *tokens*, not slips, and charges a fraction of a
           * token per slip (0.25 at the BASIC tier) — so this card can honestly say "348 slips
           * remaining" while Slip2Go's own dashboard says "87". Both are correct; they are not the
           * same question. Without this footnote that pair of numbers reads as PaymentFlow having
           * a bug, and reconciling it costs someone an afternoon (see `ProviderQuotaSchema` in
           * `packages/contracts/src/operator.ts`).
           */}
          {provider.native_remaining !== null && provider.native_unit !== null ? (
            <p className="text-2xs text-muted-foreground">
              {t('dashboard.providerQuota.nativeFootnote', {
                amount: provider.native_remaining,
                unit: provider.native_unit,
              })}
            </p>
          ) : null}
          {/*
           * Credit, and whether the vendor will actually spend it on a slip check.
           *
           * This card used to show neither, and the omission cost a real afternoon: Slip2Go's
           * package ran to zero, the card went red, the account was topped up — and the card
           * stayed red, because a top-up that lands as *credit* moves none of the token figures
           * above. There was no way to tell "the top-up has not arrived" from "the top-up arrived
           * and this panel cannot see it". Both halves are shown because credit alone does not
           * mean the vendor still works: Slip2Go spends it on slip checks only when the shop has
           * `checkSlipByCredit` set, and `statusFor` reads the same two facts together.
           */}
          {provider.credit !== null ? (
            <p className="text-2xs text-muted-foreground">
              {t('dashboard.providerQuota.credit', { amount: provider.credit })}
              {provider.credit_usable === null ? null : (
                <span className={provider.credit_usable ? undefined : TONE_CLASSES.idle.text}>
                  {' · '}
                  {t(
                    provider.credit_usable
                      ? 'dashboard.providerQuota.creditUsable'
                      : 'dashboard.providerQuota.creditUnusable',
                  )}
                </span>
              )}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * "How much slip-verification quota is left?" — one card, one row per configured provider
 * (Thunder, Slip2Go, …), for the whole platform.
 *
 * It owns its own query, and its own loading/error/empty states, in the same shape as
 * {@link ArrivalScatterCard} below: a quota lookup that is slow or 500s must not blank the KPIs
 * above it, which is exactly why this is not a field on `/metrics`.
 *
 * Unlike every other panel on this page, its hook does not call `useWebScope()` — see the doc
 * comment on `useProviderQuota` in `lib/api/ops-queries.ts` for why. Because the
 * {@link WebScopeScopedNotice} at the top of the page tells the operator that everything below it
 * is narrowed to the selected website, a platform-wide panel sitting under that notice would
 * silently contradict it if it did not say otherwise — hence the explicit "platform-wide" pill in
 * the header instead of leaving the scope ambiguous.
 */
function ProviderQuotaCard(): ReactNode {
  const { t } = useTranslation();
  const quota = useProviderQuota();

  if (quota.isLoading) return <Skeleton className="h-56" />;
  if (quota.isError) {
    return (
      <ErrorState
        error={quota.error}
        onRetry={() => {
          void quota.refetch();
        }}
      />
    );
  }
  if (!quota.data) return null;

  // Primary (position 1) first, then fallbacks in chain order, then anything configured but not
  // currently live in `SLIP_PROVIDERS` (`position: null`) last.
  const providers = [...quota.data.providers].sort((a, b) => {
    if (a.position === b.position) return 0;
    if (a.position === null) return 1;
    if (b.position === null) return -1;
    return a.position - b.position;
  });
  const needsAttention = providers.some(
    (provider) => provider.status === 'LOW' || provider.status === 'EXHAUSTED',
  );

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <Gauge className="size-4 text-muted-foreground" aria-hidden="true" />
          {t('dashboard.providerQuota.title')}
        </CardTitle>
        <div className="flex items-center gap-2">
          {needsAttention ? (
            <Pill className="bg-state-attention-bg text-state-attention">
              <AlertOctagon className="size-3" aria-hidden="true" />
              {t('dashboard.providerQuota.attentionBadge')}
            </Pill>
          ) : null}
          <Pill>{t('dashboard.providerQuota.platformWide')}</Pill>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-2xs leading-relaxed text-muted-foreground">
          {t('dashboard.providerQuota.hint')}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {providers.map((provider) => (
            <ProviderQuotaRow key={provider.provider} provider={provider} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton(): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="space-y-8" role="status" aria-label={t('dashboard.skeleton.ariaLabel')}>
      <div className="space-y-2">
        <Skeleton className="h-7 w-40" />
        <Skeleton className="h-4 w-96 max-w-full" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-44" />
        ))}
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <Skeleton className="h-64" />
        <Skeleton className="h-64 lg:col-span-2" />
      </div>
      {/*
       * Reserves the shape of `<ProviderQuotaCard>` before the KPIs (and its own query) have
       * loaded — without it, the frame the operator lands on has no room for the card, and it
       * shows up as a sudden jump once `/metrics` resolves and the card mounts to fetch its own
       * data.
       */}
      <Skeleton className="h-56" />
      {/*
       * Reserves the shape of the recon-health section (`<ReconHealthCard>` +
       * `<LedgerDivergenceStatTile>`) for the same reason as the block above — both mount only
       * once `/metrics` resolves, and each fires its own query on mount.
       */}
      <div className="grid gap-3 lg:grid-cols-3">
        <Skeleton className="h-56 lg:col-span-2" />
        <Skeleton className="h-56" />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// Recon-feed health (2026-08-27 observability pair)
// -------------------------------------------------------------------------------------------

/** Worse-first ordering for the traffic light `/recon-health` already computed server-side. */
const RECON_SEVERITY: Readonly<Record<ReconHealthRow['status'], number>> = {
  red: 2,
  yellow: 1,
  green: 0,
};

/**
 * The tone each recon-health colour maps onto, in the console's shared five-tone vocabulary.
 * Narrowed to the three tones `QUOTA_ROW_CLASSES` actually covers (`good`/`attention`/`bad`) —
 * the same three-of-five subset {@link ProviderQuotaRow} above already uses for its own status.
 */
const RECON_TONE: Readonly<Record<ReconHealthRow['status'], 'good' | 'attention' | 'bad'>> = {
  green: 'good',
  yellow: 'attention',
  red: 'bad',
};

/** One website's recon-health rows, folded into a single card entry. */
interface ReconHealthGroup {
  participantId: string;
  /** The worse of the website's feeds — see {@link RECON_SEVERITY}. */
  overall: ReconHealthRow['status'];
  /** `transactions` before `withdrawals`, mirroring `ParticipantChip`'s own ฝาก/ถอน order. */
  feeds: readonly ReconHealthRow[];
}

/**
 * Fold `/recon-health`'s flat rows into one entry per website, worst first.
 *
 * A website can report up to two rows — one per feed — and the card is meant to answer "is this
 * *website's* pipe healthy", not "is this one feed healthy", so the two rows have to become one
 * entry before anything renders. The worst feed decides the website's own colour (red beats
 * yellow beats green): a website whose deposit feed is fine and whose withdrawal feed has gone
 * silent is not a website an operator should read as "green".
 */
function groupReconHealth(items: readonly ReconHealthRow[]): ReconHealthGroup[] {
  const byParticipant = new Map<string, ReconHealthRow[]>();
  for (const row of items) {
    const feeds = byParticipant.get(row.participant_id) ?? [];
    feeds.push(row);
    byParticipant.set(row.participant_id, feeds);
  }
  const groups = [...byParticipant.entries()].map(([participantId, feeds]) => {
    const overall = feeds.reduce<ReconHealthRow['status']>(
      (worst, row) => (RECON_SEVERITY[row.status] > RECON_SEVERITY[worst] ? row.status : worst),
      'green',
    );
    const ordered = [...feeds].sort((a, b) => {
      if (a.feed === b.feed) return 0;
      return a.feed === 'transactions' ? -1 : 1;
    });
    return { participantId, overall, feeds: ordered };
  });
  // Worst-first, so the websites most worth an operator's attention are at the top of what is
  // deliberately a dense list rather than a card grid — see the JSDoc on `ReconHealthCard`.
  return groups.sort(
    (a, b) =>
      RECON_SEVERITY[b.overall] - RECON_SEVERITY[a.overall] ||
      a.participantId.localeCompare(b.participantId),
  );
}

/** "5 วินาที" / "12 นาที" / "3 ชั่วโมง" — one unit, no "ago". Callers compose the sentence. */
function formatShortDuration(t: TFunction, seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  if (whole < 60) return t('common.seconds', { count: whole });
  if (whole < 3_600) return t('common.minutes', { count: Math.round(whole / 60) });
  return t('common.hours', { count: Math.round(whole / 3_600) });
}

/**
 * One feed's status line: how long since it last polled, or — for the one status that means
 * "polling fine but stuck" — how long its cursor has gone without moving.
 *
 * `poll_gap_seconds` is used exactly as `/recon-health` sends it (already computed server-side
 * from `last_polled_at`), never recomputed from the timestamp — see the endpoint's own contract.
 */
function reconFeedLine(t: TFunction, row: ReconHealthRow, at: number): string {
  const feedLabel =
    row.feed === 'transactions'
      ? t('domain.participantChip.depositorRole')
      : t('domain.participantChip.withdrawerRole');

  // `yellow` means "polling on schedule, but a full page of backlog has outwaited the staleness
  // window with the cursor never moving" (recalibrated 2026-08-28 — a cursor parked on a partial
  // tail it re-reads every poll is the feed's normal resting shape, not this) — the number worth
  // showing there is how long it has been stuck, not how recently PF last asked. `red` (not
  // polling at all) and `green` both read off the poll age instead.
  if (row.status === 'yellow' && row.stalled) {
    if (row.cursor_advanced_at === null) {
      return t('dashboard.reconHealth.cursorNeverAdvanced', { feed: feedLabel });
    }
    const stuckSeconds = msSince(row.cursor_advanced_at, at) / 1_000;
    return t('dashboard.reconHealth.cursorStuck', {
      feed: feedLabel,
      duration: formatShortDuration(t, stuckSeconds),
    });
  }

  return t('dashboard.reconHealth.pollAge', {
    feed: feedLabel,
    age: t('common.ago', { value: formatShortDuration(t, row.poll_gap_seconds) }),
  });
}

/** One website's row inside {@link ReconHealthCard}. */
function ReconHealthWebsiteRow({
  group,
  at,
}: {
  /** The website's folded recon-health rows. */
  group: ReconHealthGroup;
  /** The instant every row's duration is measured against, so one render agrees throughout. */
  at: number;
}): ReactNode {
  const { t } = useTranslation();
  const tone = RECON_TONE[group.overall];
  return (
    <div className={cn('space-y-2 rounded-lg border p-3', QUOTA_ROW_CLASSES[tone])}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ParticipantChip participant={group.participantId} tone={tone} />
        <Pill className={TONE_CLASSES[tone].badge}>
          {t(`dashboard.reconHealth.status.${group.overall}`)}
        </Pill>
      </div>
      <ul className="space-y-1">
        {group.feeds.map((row) => (
          <li key={row.feed} className="text-2xs text-muted-foreground">
            {reconFeedLine(t, row, at)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * "Is PF still hearing from every website's own reconciliation the way it should be?"
 *
 * A dense list rather than a card grid, on purpose: the console can be scoped to one website or
 * to the whole pool (`useWebScope`), and an unscoped console can be watching a double-digit
 * number of participants at once. A grid of cards big enough to read comfortably at five entries
 * would force a scroll well before an operator reached the tenth, whereas a row per website scales
 * the same way `ProviderQuotaRow` and the Clearing page's tables already do. It owns its own query
 * and its own loading/error/empty states, in the same shape as {@link ProviderQuotaCard} and
 * {@link ArrivalScatterCard}: a slow or failed recon-health read must not take the KPIs above it
 * down with it.
 */
function ReconHealthCard(): ReactNode {
  const { t } = useTranslation();
  const query = useReconHealth();
  const at = nowMs();

  if (query.isLoading) return <Skeleton className="h-56" />;
  if (query.isError) {
    return (
      <ErrorState
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (!query.data) return null;

  const groups = groupReconHealth(query.data.items);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RadioTower className="size-4 text-muted-foreground" aria-hidden="true" />
          {t('dashboard.reconHealth.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? (
          <EmptyState
            icon={<RadioTower className="size-5" aria-hidden="true" />}
            title={t('dashboard.reconHealth.empty.title')}
            description={t('dashboard.reconHealth.empty.description')}
          />
        ) : (
          <div className="space-y-2">
            {groups.map((group) => (
              <ReconHealthWebsiteRow key={group.participantId} group={group} at={at} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// -------------------------------------------------------------------------------------------
// Ledger divergences (2026-08-27 observability pair) — the intake half
// -------------------------------------------------------------------------------------------

/** How coarsely the 24h stuck-count's lower bound is snapped — see {@link last24hSince}. */
const SINCE_BUCKET_MS = 60_000;

/**
 * The instant 24 hours ago, snapped to the current minute.
 *
 * A plain `toIso(nowMs() - hours(24))` would mint a fresh query key on *every* render this page
 * makes, and this page re-renders roughly every 15s just from its own KPI polling — turning "poll
 * every 30s" into "refetch on every unrelated re-render". Snapping to the minute keeps the key —
 * and therefore the cached result — stable across that churn while the window still rolls forward
 * the way a live "last 24h" count should, one minute at a time rather than one render at a time.
 */
function last24hSince(at: number): string {
  const bucket = Math.floor(at / SINCE_BUCKET_MS) * SINCE_BUCKET_MS;
  return toIso(bucket - hours(24));
}

/** Rows `/ledger-divergences` will actually hand back before the count is reported as a floor. */
const DIVERGENCE_COUNT_CEILING = 200;

/**
 * "Has any website's own ledger reconciliation flagged something in the last day?" — a stat tile,
 * not a table: this card exists to say *whether* an operator needs to look, and the full list
 * page (linked from it) is where they look.
 *
 * It owns its own query for the same reason every other independent panel on this page does: a
 * slow or failed read here must not blank the KPIs above it. The 24h window is computed
 * client-side (see {@link last24hSince}) because the endpoint takes an absolute `since`, not a
 * rolling `window_hours` the server would resolve the way `/metrics` does.
 *
 * `/ledger-divergences` reports no total separate from the rows it actually returns, so a window
 * with 200+ matches cannot be counted precisely at the ceiling this asks for — it is shown as
 * `200+` rather than as a number the endpoint never actually promised.
 */
function LedgerDivergenceStatTile(): ReactNode {
  const { t } = useTranslation();
  const since = last24hSince(nowMs());
  const divergences = useLedgerDivergences({ since, limit: DIVERGENCE_COUNT_CEILING });

  if (divergences.isLoading) return <Skeleton className="h-32" />;
  if (divergences.isError) {
    return (
      <ErrorState
        error={divergences.error}
        onRetry={() => {
          void divergences.refetch();
        }}
      />
    );
  }
  if (!divergences.data) return null;

  const count = divergences.data.items.length;
  const displayValue =
    count >= DIVERGENCE_COUNT_CEILING ? `${String(DIVERGENCE_COUNT_CEILING)}+` : String(count);

  return (
    <StatTile
      label={t('dashboard.ledgerDivergences.label')}
      value={displayValue}
      hint={t('dashboard.ledgerDivergences.hint')}
      // Same treatment as the DLQ tile above: a non-zero count is an incident, not a metric, so
      // it does not sit in the same grey as everything else on the page.
      className={count > 0 ? 'border-state-bad/40 bg-state-bad-bg/30' : undefined}
      badge={
        <Button asChild variant="ghost" size="sm" className="-my-1 h-7">
          <Link to="/ops/ledger-divergences">
            {t('dashboard.ledgerDivergences.link')}
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      }
    />
  );
}
